/**
 * @fileoverview Connect evidence-backed client-update drafts to explicit staff edit, accept, retry, and discard actions. Backend work/draft versions protect current content and accepted transitions. This modal owns editable text and work-status presentation; acceptance and actual message delivery remain separately owned execution boundaries.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../api';
import { AppButton, AppIcon, AppModal } from '../shared';
import './ClientUpdateWorkModal.css';

const statusCopy = {
  queued: 'Queued',
  gathering: 'Gathering matter evidence',
  needs_input: 'Needs input',
  ready_for_review: 'Ready for review',
  accepted: 'Accepted',
  discarded: 'Discarded',
  failed: 'Could not prepare draft',
  stale: 'Matter record changed',
};



function errorMessage(error, fallback) {
  return error?.response?.data?.error || fallback;
}



function ClientUpdateWorkModal({ matterNumber, visible, onClose, showToast }) {
  const [workItem, setWorkItem] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState('');
  const [error, setError] = useState('');

  const applyWorkItem = useCallback(

  (item) => {
    setWorkItem(item || null);
    setSubject(item?.draft?.subject || '');
    setBody(item?.draft?.body || '');
  }, []);

  const loadOrCreate = useCallback(

  async () => {
    setLoading(true);
    setError('');
    try {
      const active = await api.get(`/matters/${encodeURIComponent(matterNumber)}/client-update-work/active`);
      if (active.data?.work_item) {
        applyWorkItem(active.data.work_item);
        return;
      }
      const created = await api.post(`/matters/${encodeURIComponent(matterNumber)}/client-update-work`);
      applyWorkItem(created.data?.work_item);
    } catch (requestError) {
      setError(errorMessage(requestError, 'The client update review could not be prepared.'));
    } finally {
      setLoading(false);
    }
  }, [applyWorkItem, matterNumber]);

  useEffect(() => {
    if (visible && matterNumber) loadOrCreate();
    if (!visible) {
      setWorkItem(null);
      setError('');
    }
  }, [visible, matterNumber, loadOrCreate]);

  const isDirty = workItem?.status === 'ready_for_review' && (
    subject !== (workItem?.draft?.subject || '') || body !== (workItem?.draft?.body || '')
  );

  const citedIds = useMemo(
    () => new Set((workItem?.draft?.citations || []).map((citation) => citation.evidence_id)),
    [workItem]
  );

  const runAction =

  async (name, callback) => {
    setAction(name);
    setError('');
    try {
      const response = await callback();
      applyWorkItem(response.data?.work_item);
      return response.data?.work_item;
    } catch (requestError) {
      setError(errorMessage(requestError, `The ${name} action could not be completed.`));
      return null;
    } finally {
      setAction('');
    }
  };

  const saveDraft =

  () => runAction('save', () => api.put(`/client-update-work/${workItem.id}`, {
    subject,
    body,
    draft_version: workItem.draft.version,
  }));

  const acceptDraft =

  async () => {
    setAction('accept');
    setError('');
    try {
      let current = workItem;
      if (isDirty) {
        const saved = await api.put(`/client-update-work/${workItem.id}`, {
          subject,
          body,
          draft_version: workItem.draft.version,
        });
        current = saved.data.work_item;
      }
      const accepted = await api.post(`/client-update-work/${current.id}/accept`, {
        draft_version: current.draft.version,
      });
      applyWorkItem(accepted.data.work_item);
      showToast?.({ severity: 'success', summary: 'Draft accepted', detail: 'No message was sent.' });
    } catch (requestError) {
      setError(errorMessage(requestError, 'The draft could not be accepted.'));
      if (requestError?.response?.data?.code === 'sources_changed') {
        await loadOrCreate();
      }
    } finally {
      setAction('');
    }
  };

  const retry =

  () => runAction('retry', () => api.post(`/client-update-work/${workItem.id}/retry`));
  const discard =

  () => runAction('discard', () => api.post(`/client-update-work/${workItem.id}/discard`));

  const footer = workItem?.status === 'ready_for_review' ? (
    <>
      <AppButton label="Discard" variant="secondary" disabled={Boolean(action)} onClick={discard} />
      <AppButton label="Save draft" variant="secondary" disabled={!isDirty || Boolean(action)} onClick={saveDraft} />
      <AppButton label="Accept draft" icon="pi pi-check" disabled={!subject.trim() || !body.trim() || Boolean(action)} onClick={acceptDraft} />
    </>
  ) : workItem && ['needs_input', 'failed', 'stale'].includes(workItem.status) ? (
    <>
      <AppButton label="Discard" variant="secondary" disabled={Boolean(action)} onClick={discard} />
      <AppButton label="Retry" icon="pi pi-refresh" disabled={Boolean(action)} onClick={retry} />
    </>
  ) : workItem && ['accepted', 'discarded'].includes(workItem.status) ? (
    <AppButton label="Close" variant="secondary" onClick={onClose} />
  ) : null;

  return (
    <AppModal
      visible={visible}
      title="Client status update"
      description="Review and edit this draft. Accepting it does not send a message."
      onClose={onClose}
      footer={footer}
      width="1040px"
      className="client-update-work"
    >
      {loading && (
        <div className="client-update-work__state" role="status">
          <AppIcon icon="pi pi-spin pi-spinner" aria-hidden="true" />
          <span>Preparing a bounded matter summary…</span>
        </div>
      )}

      {error && (
        <div className="client-update-work__alert" role="alert">
          <AppIcon icon="pi pi-exclamation-circle" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      {!loading && workItem && (
        <>
          <div className={`client-update-work__status is-${workItem.status}`}>
            <span className="client-update-work__status-dot" aria-hidden="true" />
            <span>{statusCopy[workItem.status] || workItem.status}</span>
          </div>

          {workItem.status === 'ready_for_review' && (
            <div className="client-update-work__layout">
              <section className="client-update-work__draft" aria-label="Client update draft">
                <div className="client-update-work__recipient">
                  <span className="client-update-work__label">To</span>
                  <strong>{workItem.recipient?.name}</strong>
                  <span>{workItem.recipient?.email}</span>
                </div>
                <label className="client-update-work__field">
                  <span>Subject</span>
                  <input value={subject} maxLength={240} onChange={(event) => setSubject(event.target.value)} />
                </label>
                <label className="client-update-work__field">
                  <span>Message</span>
                  <textarea value={body} maxLength={12000} rows={15} onChange={(event) => setBody(event.target.value)} />
                </label>
                <div className="client-update-work__no-send">
                  <AppIcon icon="pi pi-lock" aria-hidden="true" />
                  <span>Review only — this screen cannot email or text the client.</span>
                </div>
              </section>

              <aside className="client-update-work__evidence" aria-label="Matter evidence">
                <div className="client-update-work__evidence-heading">
                  <span>Evidence</span>
                  <small>{workItem.evidence?.length || 0} records</small>
                </div>
                <div className="client-update-work__evidence-list">
                  {(workItem.evidence || []).map((record) => (
                    <a key={record.id} href={record.href} className="client-update-work__evidence-card">
                      <span className="client-update-work__evidence-topline">
                        <span className="client-update-work__evidence-kind">{record.kind.replace('_', ' ')}</span>
                        {citedIds.has(record.id) && <span className="client-update-work__used">Used</span>}
                      </span>
                      <strong>{record.title}</strong>
                      <span>{record.summary}</span>
                    </a>
                  ))}
                </div>
              </aside>
            </div>
          )}

          {workItem.status === 'needs_input' && (
            <div className="client-update-work__blocked">
              <AppIcon icon="pi pi-pencil" aria-hidden="true" />
              <div>
                <strong>The matter record needs attention</strong>
                {(workItem.blocking_questions || []).map((question) => <p key={question}>{question}</p>)}
              </div>
            </div>
          )}

          {workItem.status === 'stale' && (
            <div className="client-update-work__blocked">
              <AppIcon icon="pi pi-history" aria-hidden="true" />
              <div>
                <strong>The matter record changed</strong>
                <p>Regenerate the draft before accepting it.</p>
              </div>
            </div>
          )}

          {workItem.status === 'failed' && (
            <div className="client-update-work__blocked">
              <AppIcon icon="pi pi-refresh" aria-hidden="true" />
              <div><strong>Draft generation stopped safely</strong><p>{workItem.failure?.message}</p></div>
            </div>
          )}

          {['accepted', 'discarded'].includes(workItem.status) && (
            <div className="client-update-work__complete">
              <AppIcon icon={workItem.status === 'accepted' ? 'pi pi-check-circle' : 'pi pi-trash'} aria-hidden="true" />
              <div>
                <strong>{workItem.status === 'accepted' ? 'Draft accepted' : 'Draft discarded'}</strong>
                <p>No client communication was sent and no reminder state was changed.</p>
              </div>
            </div>
          )}
        </>
      )}
    </AppModal>
  );
}

export default ClientUpdateWorkModal;
